import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import tempMonitoringService from '../../services/tempMonitoringService';

const cToF = c => c != null ? (c * 9 / 5 + 32).toFixed(1) : '';
const fToC = f => f !== '' && f != null ? ((Number(f) - 32) * 5 / 9) : null;

const EMPTY_SENSOR = {
  name: '', locationName: '', provider: 'yolink',
  providerDeviceId: '', providerHomeId: '',
  minTemp: '', maxTemp: '', displayUnit: 'F',
  consecutiveThreshold: 2, notes: '',
};

export default function TempMonitoringConfigPage() {
  const [sensors, setSensors]           = useState([]);
  const [creds, setCreds]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');

  // Sensor modal
  const [showSensorModal, setShowSensorModal] = useState(false);
  const [editSensor, setEditSensor]           = useState(null);
  const [form, setForm]                       = useState(EMPTY_SENSOR);
  const [saving, setSaving]                   = useState(false);

  // YoLink credentials
  const [uaid, setUaid]     = useState('');
  const [secret, setSecret] = useState('');
  const [savingCred, setSavingCred] = useState(false);

  // Poll interval
  const [pollInterval, setPollInterval]   = useState(15);
  const [savingInterval, setSavingInterval] = useState(false);

  // Device discovery
  const [testing, setTesting]           = useState(false);
  const [discovered, setDiscovered]     = useState(null);
  const [syncing, setSyncing]           = useState(false);

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const c = creds.find(c => c.provider === 'yolink');
    if (c) setPollInterval(c.pollIntervalMinutes || 15);
  }, [creds]);

  async function load() {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        tempMonitoringService.getSensors(),
        tempMonitoringService.getCredentials(),
      ]);
      setSensors(s);
      setCreds(c);
    } catch { setError('Failed to load configuration.'); }
    finally { setLoading(false); }
  }

  function flash(msg) { setSuccess(msg); setTimeout(() => setSuccess(''), 4000); }

  // ── Sensor form ──────────────────────────────────────────────────────────────

  function openNew() {
    setEditSensor(null);
    setForm(EMPTY_SENSOR);
    setShowSensorModal(true);
  }

  function openEdit(s) {
    setEditSensor(s);
    const unit = s.displayUnit || 'F';
    setForm({
      name:                 s.name,
      locationName:         s.locationName || '',
      provider:             s.provider,
      providerDeviceId:     s.providerDeviceId || '',
      providerHomeId:       s.providerHomeId   || '',
      minTemp:              s.minTempC != null ? (unit === 'F' ? cToF(s.minTempC) : String(s.minTempC)) : '',
      maxTemp:              s.maxTempC != null ? (unit === 'F' ? cToF(s.maxTempC) : String(s.maxTempC)) : '',
      displayUnit:          unit,
      consecutiveThreshold: s.consecutiveThreshold || 2,
      notes:                s.notes || '',
    });
    setShowSensorModal(true);
  }

  async function saveSensor() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const minTempC = form.minTemp !== '' ? (form.displayUnit === 'F' ? fToC(form.minTemp) : Number(form.minTemp)) : null;
      const maxTempC = form.maxTemp !== '' ? (form.displayUnit === 'F' ? fToC(form.maxTemp) : Number(form.maxTemp)) : null;

      const payload = {
        name:                 form.name.trim(),
        locationName:         form.locationName.trim() || null,
        provider:             form.provider,
        providerDeviceId:     form.providerDeviceId || null,
        providerHomeId:       form.providerHomeId   || null,
        minTempC, maxTempC,
        consecutiveThreshold: Number(form.consecutiveThreshold) || 2,
        displayUnit:          form.displayUnit,
        notes:                form.notes.trim() || null,
      };

      if (editSensor) {
        await tempMonitoringService.updateSensor(editSensor.id, payload);
        flash('Sensor updated.');
      } else {
        await tempMonitoringService.createSensor(payload);
        flash('Sensor added.');
      }

      setShowSensorModal(false);
      load();
    } catch { setError('Failed to save sensor.'); }
    finally { setSaving(false); }
  }

  async function deactivateSensor(s) {
    if (!window.confirm(`Deactivate "${s.name}"? Readings will be preserved.`)) return;
    try {
      await tempMonitoringService.deleteSensor(s.id);
      flash('Sensor deactivated.');
      load();
    } catch { setError('Failed to deactivate sensor.'); }
  }

  // ── Credentials ───────────────────────────────────────────────────────────────

  async function saveCred() {
    if (!uaid.trim() || !secret.trim()) return;
    setSavingCred(true);
    try {
      await tempMonitoringService.saveCredentials('yolink', { uaid: uaid.trim(), secret: secret.trim() });
      setSecret('');
      flash('YoLink credentials saved.');
      load();
    } catch { setError('Failed to save credentials.'); }
    finally { setSavingCred(false); }
  }

  async function saveInterval() {
    setSavingInterval(true);
    try {
      await tempMonitoringService.saveCredentialSettings('yolink', { pollIntervalMinutes: pollInterval });
      flash(`Poll interval set to ${pollInterval} min.`);
    } catch { setError('Failed to save poll interval.'); }
    finally { setSavingInterval(false); }
  }

  async function testConn() {
    setTesting(true);
    setDiscovered(null);
    try {
      const res = await tempMonitoringService.testCredentials('yolink');
      setDiscovered(res.devices || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Connection test failed.');
    } finally { setTesting(false); }
  }

  async function addDiscoveredDevice(d) {
    try {
      await tempMonitoringService.createSensor({
        name:             d.name || d.deviceId,
        provider:         'yolink',
        providerDeviceId: d.deviceId,
        providerHomeId:   d.homeToken,
        displayUnit:      'F',
        consecutiveThreshold: 2,
      });
      flash(`Sensor "${d.name || d.deviceId}" added.`);
      load();
    } catch { setError('Failed to add sensor.'); }
  }

  async function syncNow() {
    setSyncing(true);
    try {
      await tempMonitoringService.syncNow('yolink');
      flash('Sync started — readings will appear within seconds.');
    } catch { setError('Sync failed.'); }
    finally { setSyncing(false); }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  const yolinkCred = creds.find(c => c.provider === 'yolink');
  const activeSensors   = sensors.filter(s => s.isActive);
  const inactiveSensors = sensors.filter(s => !s.isActive);

  function tempDisplay(sensor) {
    const u = sensor.displayUnit || 'F';
    const min = sensor.minTempC != null ? (u === 'F' ? `${cToF(sensor.minTempC)} °F` : `${sensor.minTempC} °C`) : '—';
    const max = sensor.maxTempC != null ? (u === 'F' ? `${cToF(sensor.maxTempC)} °F` : `${sensor.maxTempC} °C`) : '—';
    return `${min} / ${max}`;
  }

  if (loading) return (
    <div className="container mt-4 text-center text-muted py-5">
      <div className="spinner-border spinner-border-sm me-2" />
      Loading…
    </div>
  );

  return (
    <div className="container mt-4 pb-5" style={{ maxWidth: 900 }}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">Temperature Monitoring — Config</h4>
        <Link to="/sqf/temp-monitoring" className="btn btn-outline-secondary btn-sm">← Dashboard</Link>
      </div>

      {error   && <div className="alert alert-danger   py-2">{error}   <button className="btn-close float-end btn-sm" onClick={() => setError('')} /></div>}
      {success && <div className="alert alert-success  py-2">{success}</div>}

      {/* ── YoLink Credentials ─────────────────────────────────────────────── */}
      <div className="card shadow-sm mb-4">
        <div className="card-header d-flex align-items-center justify-content-between">
          <span className="fw-semibold">YoLink Connection</span>
          {yolinkCred && (
            <span className={`badge bg-${yolinkCred.lastSyncStatus === 'ok' ? 'success' : yolinkCred.lastSyncStatus === 'error' ? 'danger' : 'secondary'}`}>
              {yolinkCred.lastSyncStatus || 'not synced'}
            </span>
          )}
        </div>
        <div className="card-body">
          <p className="text-muted mb-3" style={{ fontSize: 13 }}>
            Get your UAID and Secret Key from the YoLink app: <strong>Settings → Account → Advanced Settings → User Access Credentials</strong>.
          </p>
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>UAID</label>
              <input className="form-control form-control-sm" value={uaid} onChange={e => setUaid(e.target.value)} placeholder={yolinkCred ? '(saved)' : 'UA_XXXXXXXX'} />
            </div>
            <div className="col-md-6">
              <label className="form-label mb-1" style={{ fontSize: 12 }}>Secret Key</label>
              <input className="form-control form-control-sm" type="password" value={secret} onChange={e => setSecret(e.target.value)} placeholder="••••••••" />
            </div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button className="btn btn-primary btn-sm" onClick={saveCred} disabled={savingCred || !uaid || !secret}>
              {savingCred ? 'Saving…' : 'Save Credentials'}
            </button>
            {yolinkCred && (
              <>
                <button className="btn btn-outline-secondary btn-sm" onClick={testConn} disabled={testing}>
                  {testing ? 'Testing…' : 'Test & Discover Sensors'}
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={syncNow} disabled={syncing}>
                  {syncing ? 'Syncing…' : 'Sync Now'}
                </button>
                <span className="text-muted" style={{ fontSize: 12 }}>
                  Historical import requires{' '}
                  <a href="mailto:service@yosmart.com?subject=DEVICE.HISTORICAL_DATA.READ%20permission%20request" target="_blank" rel="noreferrer">
                    DEVICE.HISTORICAL_DATA.READ
                  </a>
                  {' '}permission from YoLink support.
                </span>
              </>
            )}
          </div>

          {yolinkCred?.lastSyncAt && (
            <p className="text-muted mt-2 mb-0" style={{ fontSize: 11 }}>
              Last sync: {new Date(yolinkCred.lastSyncAt).toLocaleString()}
              {yolinkCred.lastSyncError && <span className="text-danger ms-2">{yolinkCred.lastSyncError}</span>}
            </p>
          )}

          {yolinkCred && (
            <div className="mt-3 pt-3 border-top">
              <label className="form-label mb-1">
                Poll interval: <strong>every {pollInterval >= 60 ? `${pollInterval / 60} hr${pollInterval > 60 ? 's' : ''}` : `${pollInterval} min`}</strong>
              </label>
              <input
                type="range" className="form-range"
                min="15" max="120" step="15"
                value={pollInterval}
                onChange={e => setPollInterval(Number(e.target.value))}
              />
              <div className="d-flex justify-content-between mb-2" style={{ fontSize: 11, color: '#888' }}>
                <span>15 min</span><span>30 min</span><span>45 min</span><span>1 hr</span><span>1.5 hrs</span><span>2 hrs</span>
              </div>
              <button className="btn btn-outline-primary btn-sm" onClick={saveInterval} disabled={savingInterval}>
                {savingInterval ? 'Saving…' : 'Save Interval'}
              </button>
            </div>
          )}

          {/* Discovered devices */}
          {discovered !== null && (
            <div className="mt-3">
              {discovered.length === 0 ? (
                <div className="text-muted" style={{ fontSize: 13 }}>No THSensor devices found in your YoLink account.</div>
              ) : (
                <>
                  <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>Found {discovered.length} sensor(s):</div>
                  <div className="list-group">
                    {discovered.map(d => {
                      const already = sensors.some(s => s.providerDeviceId === d.deviceId);
                      return (
                        <div key={d.deviceId} className="list-group-item d-flex align-items-center justify-content-between py-2">
                          <div>
                            <div className="fw-semibold" style={{ fontSize: 13 }}>{d.name || d.deviceId}</div>
                            <div className="text-muted" style={{ fontSize: 11 }}>ID: {d.deviceId}</div>
                          </div>
                          {already
                            ? <span className="badge bg-secondary">Already added</span>
                            : <button className="btn btn-success btn-sm" onClick={() => addDiscoveredDevice(d)}>+ Add</button>
                          }
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Sensors ──────────────────────────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="mb-0">Sensors ({activeSensors.length} active)</h6>
        <button className="btn btn-primary btn-sm" onClick={openNew}>+ Add Sensor</button>
      </div>

      {activeSensors.length === 0 ? (
        <div className="text-muted text-center py-4 border rounded" style={{ fontSize: 13 }}>
          No active sensors. Add one manually or connect YoLink and use "Test & Discover".
        </div>
      ) : (
        <div className="table-responsive mb-3">
          <table className="table table-sm table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Location</th>
                <th>Provider</th>
                <th>Min / Max</th>
                <th>Flag after</th>
                <th>Last reading</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {activeSensors.map(s => (
                <tr key={s.id}>
                  <td className="fw-semibold" style={{ fontSize: 13 }}>{s.name}</td>
                  <td style={{ fontSize: 12 }}>{s.locationName || '—'}</td>
                  <td><span className="badge bg-secondary" style={{ fontSize: 10 }}>{s.provider}</span></td>
                  <td style={{ fontSize: 12 }}>{tempDisplay(s)}</td>
                  <td style={{ fontSize: 12 }}>{s.consecutiveThreshold}×</td>
                  <td style={{ fontSize: 12 }}>
                    {s.lastReadAt
                      ? <>{s.displayUnit === 'F' ? `${cToF(s.lastTempC)} °F` : `${s.lastTempC} °C`} <span className="text-muted">· {new Date(s.lastReadAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span></>
                      : <span className="text-muted">No readings yet</span>
                    }
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => openEdit(s)}>Edit</button>
                      <button className="btn btn-outline-danger btn-sm"    style={{ fontSize: 11 }} onClick={() => deactivateSensor(s)}>Deactivate</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inactiveSensors.length > 0 && (
        <details className="mb-3">
          <summary className="text-muted" style={{ fontSize: 12, cursor: 'pointer' }}>Show {inactiveSensors.length} inactive sensor(s)</summary>
          <div className="mt-2">
            {inactiveSensors.map(s => (
              <div key={s.id} className="d-flex align-items-center justify-content-between py-1 border-bottom">
                <span className="text-muted" style={{ fontSize: 13 }}>{s.name} {s.locationName && `— ${s.locationName}`}</span>
                <button className="btn btn-link btn-sm p-0" style={{ fontSize: 11 }} onClick={async () => {
                  await tempMonitoringService.updateSensor(s.id, { isActive: true });
                  flash(`${s.name} reactivated.`); load();
                }}>Reactivate</button>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* ── Sensor modal ──────────────────────────────────────────────────────── */}
      {showSensorModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editSensor ? 'Edit Sensor' : 'Add Sensor'}</h5>
                <button className="btn-close" onClick={() => setShowSensorModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Name <span className="text-danger">*</span></label>
                  <input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Walk-in Cooler #1" />
                </div>
                <div className="mb-3">
                  <label className="form-label">Location / Zone</label>
                  <input className="form-control" value={form.locationName} onChange={e => setForm(f => ({ ...f, locationName: e.target.value }))} placeholder="Production Floor" />
                </div>
                <div className="row g-2 mb-3">
                  <div className="col">
                    <label className="form-label">Provider</label>
                    <select className="form-select" value={form.provider} onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}>
                      <option value="manual">Manual</option>
                      <option value="yolink">YoLink</option>
                      <option value="thermoworks">ThermoWorks (future)</option>
                    </select>
                  </div>
                  <div className="col">
                    <label className="form-label">Display Unit</label>
                    <select className="form-select" value={form.displayUnit} onChange={e => setForm(f => ({ ...f, displayUnit: e.target.value }))}>
                      <option value="F">°F</option>
                      <option value="C">°C</option>
                    </select>
                  </div>
                </div>

                {form.provider === 'yolink' && (
                  <div className="row g-2 mb-3">
                    <div className="col">
                      <label className="form-label" style={{ fontSize: 12 }}>YoLink Device ID</label>
                      <input className="form-control form-control-sm" value={form.providerDeviceId} onChange={e => setForm(f => ({ ...f, providerDeviceId: e.target.value }))} placeholder="D88B..."/>
                    </div>
                    <div className="col">
                      <label className="form-label" style={{ fontSize: 12 }}>Home Token</label>
                      <input className="form-control form-control-sm" value={form.providerHomeId} onChange={e => setForm(f => ({ ...f, providerHomeId: e.target.value }))} placeholder="xxxxxx"/>
                    </div>
                  </div>
                )}

                <div className="row g-2 mb-3">
                  <div className="col">
                    <label className="form-label">Min Temp ({form.displayUnit === 'F' ? '°F' : '°C'})</label>
                    <input type="number" step="0.1" className="form-control" value={form.minTemp}
                      onChange={e => setForm(f => ({ ...f, minTemp: e.target.value }))} placeholder="e.g. 33" />
                    <div className="form-text">Leave blank for no lower limit</div>
                  </div>
                  <div className="col">
                    <label className="form-label">Max Temp ({form.displayUnit === 'F' ? '°F' : '°C'})</label>
                    <input type="number" step="0.1" className="form-control" value={form.maxTemp}
                      onChange={e => setForm(f => ({ ...f, maxTemp: e.target.value }))} placeholder="e.g. 40" />
                    <div className="form-text">Leave blank for no upper limit</div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Flag after <strong>{form.consecutiveThreshold}</strong> consecutive out-of-range reading(s)</label>
                  <input type="range" className="form-range" min="1" max="8" value={form.consecutiveThreshold}
                    onChange={e => setForm(f => ({ ...f, consecutiveThreshold: Number(e.target.value) }))} />
                  <div className="d-flex justify-content-between" style={{ fontSize: 11, color: '#888' }}>
                    <span>1×</span><span>4×</span><span>8×</span>
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Location detail, sensor quirks…" />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowSensorModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={saveSensor} disabled={saving || !form.name.trim()}>
                  {saving ? 'Saving…' : editSensor ? 'Save Changes' : 'Add Sensor'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
